import { AlertManager } from "client/Hooks";
import {
	OpenFilePossibilitiesProvider
} from "./openFilePossibilitiesProvider";
import { open as openInWopi } from "client_legacy/Documents/WebDav/wopiOpener";
import { open as openInLocalApp } from "client_legacy/Documents/WebDav/localAppOpener";
import {
	Action,
	ActionKind,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { getExtension } from "client_legacy/Documents/path";
import { getOpenLocalizationsForExtension, OpenLocalizations } from "client_legacy/Documents/openLocalizations";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import {
	documentsConfiguration,
	WebDavConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	OfficeDocumentEditor,
	NewDocumentType
} from "client/Api/Documents/common";
import { getItemsInPreferredOrder, UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { isNotNil } from "client/Misc/utils";
import { removeBookmarks } from "client/Api/Documents/Bookmarks/bookmarks";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { download, openInline } from "client_legacy/ndaw/fileDownloader";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { sendWebDavFileAsAttachment } from "client_legacy/Email/SendFile/sendFile";
import { openImageDialog } from "./WebDavImagePubSubWrapper";
import { openRenameDialog as openRenameWebDavDialog } from "../WebDav/Dialogs/RenameDialog";
import { openNewFolderDialog } from "./Dialogs/NewFolderDialog";
import { openNewDocumentDialog } from "./Dialogs/NewDocumentDialog";
import { FileSystemItemModel, WebDavServerModel } from "client/Api/Documents/WebDav/webDav";
import { openSetOfficePreferenceDialog } from "../SetOfficePreferenceDialog";
import { openDeleteDocumentsDialog } from "./Dialogs/DeleteDocumentsDialog";
import { BookmarkEvent, publish } from "client_legacy/Documents/Bookmarks/events";
import {
	ToolbarAction,
	ToolbarActionGroup
} from "client/Components";
import { openUrlFile } from "../openUrlFile";

export type WebDavDocumentActionContext = {
	server: WebDavServerModel;
	folder: {
		id: string;
		name: string;
	};
	selectedDocuments: FileSystemItemModel[];
	isMobile: boolean;
	isInSelectMode: boolean;
};

export type WebDavDocumentAction = Action<WebDavDocumentActionContext> & {
	quickActionIndex?: number;
};

export type WebDavDocumentActionAndOthers = ToolbarAction<WebDavDocumentActionContext>;

const _newGroup: ToolbarActionGroup = {
	kind: "regular",
	name: "New",
	title: localize("_New"),
	icon: "plus",
	isPrimary: true
};

const _openGroup: ToolbarActionGroup = {
	kind: "alternative",
	name: "open-group",
	title: localize("_Open"),
	icon: "folder-open"
};

const _manageGroup: ToolbarActionGroup = {
	kind: "virtual",
	name: "manage-group"
};

const _uploadGroup: ToolbarActionGroup = {
	kind: "regular",
	name: "upload-group",
	icon: "upload",
	title: localize("_Upload")
};

export type FolderActionHandlers = {
	enableSelectMode(): void;
	disableSelectMode(): void;
	selectAll(): void;
	deselectAll(): void;
	uploadFolder(): void;
	uploadFiles(): void;
};

export type SelectionActionHandlers = OpenActionHandlers & {
	onMoveItems: (selection: readonly FileSystemItemModel[]) => void;
	onCopyItems: (selection: readonly FileSystemItemModel[]) => void;
	addBookmark: AddBookmarkHandler;
};

export type ActionHandlers = SelectionActionHandlers & FolderActionHandlers;

export function getNewActions(
	webDavConfiguration: WebDavConfiguration,
	shouldHideOnSelection: boolean
): readonly WebDavDocumentActionAndOthers[] {
	return [
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "NewFolder",
			icon: { kind: "svg", name: "folder" },
			title: localize("_Folder"),
			canExecute: (c) => !shouldHideOnSelection || c.selectedDocuments.length === 0,
			execute: (context) => {
				openNewFolderDialog({
					serverId: context.server
						.id,
					folderId: context.folder
						.id
				});
			}
		},
		{
			kind: ActionKind.Divider,
			group: _newGroup,
			name: "NewDivider",
			canExecute: (c) => !shouldHideOnSelection || c.selectedDocuments.length === 0
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "Word",
			icon: { kind: "svg", name: "docx" },
			title: localize("_WordDocument"),
			canExecute: (c) => !shouldHideOnSelection || c.selectedDocuments.length === 0,
			execute: (context) => {
				openNewDocumentDialog({
					server: context.server,
					folderId: context.folder
						.id,
					documentType: NewDocumentType.Word,
					webDavConfiguration
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "Excel",
			icon: { kind: "svg", name: "xlsx" },
			title: localize("_ExcelWorkbook"),
			canExecute: (c) => !shouldHideOnSelection || c.selectedDocuments.length === 0,
			execute: (context) => {
				openNewDocumentDialog({
					server: context.server,
					folderId: context.folder
						.id,
					documentType: NewDocumentType.Excel,
					webDavConfiguration
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _newGroup,
			name: "PowerPoint",
			icon: { kind: "svg", name: "powerpoint" },
			title: localize("_PowerPointPresentation"),
			canExecute: (c) => !shouldHideOnSelection || c.selectedDocuments.length === 0,
			execute: (context) => {
				openNewDocumentDialog({
					server: context.server,
					folderId: context.folder
						.id,
					documentType: NewDocumentType.PowerPoint,
					webDavConfiguration
				});
			}
		}
	];
}

export function getFolderActions(
	itemsOnPage: readonly FileSystemItemModel[],
	handlers: FolderActionHandlers,
	includeNewActions: boolean,
	webDavConfiguration: WebDavConfiguration
): readonly WebDavDocumentActionAndOthers[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "Select",
			title: localize("_Select"),
			icon: "hand-pointer",
			canExecute: (c) => {
				return c.isMobile
					&& !c.isInSelectMode
					&& itemsOnPage.length > 0;
			},
			execute: () => {
				handlers.enableSelectMode();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "Cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: (c) => {
				return c.isMobile && c.isInSelectMode;
			},
			execute: () => {
				handlers.disableSelectMode();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "DeselectAll",
			title: (c) => deselectAllTitle(c.selectedDocuments),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedDocuments.length > 0 && (!c.isMobile || c.isInSelectMode),
			execute: () => handlers.deselectAll()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedDocuments.length > 0
		},
		{
			kind: ActionKind.Regular,
			name: "SelectAll",
			title: localize("_SelectAll"),
			icon: "circle-check",
			canExecute: (c) => {
				return c.isMobile
					&& c.isInSelectMode
					&& c.selectedDocuments.length !== itemsOnPage.length;
			},
			execute: () => {
				handlers.selectAll();
			}
		},
		...(includeNewActions ? getNewActions(webDavConfiguration, true) : []),
		{
			kind: ActionKind.Regular,
			name: "Upload folder",
			icon: {
				kind: "svg",
				name: "folder"
			},
			title: localize("_Folder"),
			group: _uploadGroup,
			canExecute: (c) => c.selectedDocuments.length === 0,
			execute: () => {
				handlers.uploadFolder();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "Upload files",
			icon: "file",
			title: localize("_Files"),
			group: _uploadGroup,
			canExecute: (c) => c.selectedDocuments.length === 0,
			execute: () => {
				handlers.uploadFiles();
			}
		},
	];
}

const _openFilePossibilitiesProvider = new OpenFilePossibilitiesProvider();

const _downloadAction: WebDavDocumentAction = {
	kind: ActionKind.Regular,
	name: "Download",
	title: localize("_Download"),
	icon: "download",
	canExecute: (context) => {
		const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
		if (documents.length !== 1) {
			return false;
		}

		return !documents[0].isFolder;
	},
	execute: (context) => {
		const serverId: number = context.server.id;
		const file: FileSystemItemModel = context.selectedDocuments[0];

		const downloadFileUrl: string = getFileDownloadUrl(file.id, serverId);

		download(downloadFileUrl);
	}
};

type AddBookmarkHandler = (server: WebDavServerModel, item: FileSystemItemModel) => void;

function createAddBookmarkAction(handler: AddBookmarkHandler): WebDavDocumentAction {
	return {
		kind: ActionKind.Regular,
		name: "AddBookmark",
		title: localize("_AddBookmark"),
		icon: "star-filled",
		canExecute: (context) => {
			return context.selectedDocuments.length === 1
				&& context.selectedDocuments[0].bookmarkId === null;
		},
		execute: (context) => {
			handler(
				context.server,
				context.selectedDocuments[0]
			);
		},
		quickActionIndex: 1
	};
}

const createRemoveBookmarkAction = (alertManager: AlertManager): WebDavDocumentAction => ({
	kind: ActionKind.Regular,
	name: "RemoveBookmark",
	title: localize("_RemoveBookmark"),
	icon: "star",
	canExecute: (context) => {
		return context.selectedDocuments.length > 0
			&& context.selectedDocuments
				.every(x => x.bookmarkId !== null);
	},
	execute: (context) => {
		const bookmarkIds: number[] = context.selectedDocuments
			.map(x => x.bookmarkId)
			.filter(isNotNil);

		const bookmarkNames: string[] = context.selectedDocuments.map(x => "'" + x.name + "'");

		removeBookmarks(bookmarkIds)
			.then(() => {
				alertManager.showSuccess(
					localize("_BookmarksRemovedForItemNames")
						.replace("{ItemNames}", bookmarkNames.join(", "))
				);

				publish(BookmarkEvent.Deleted, bookmarkIds);
			});
	},
	quickActionIndex: 0
});

const _shareActions: readonly WebDavDocumentAction[] = [
	{
		kind: ActionKind.Regular,
		name: "AttachToEmail",
		title: localize("_AttachToEmail"),
		icon: "envelope",
		canExecute: (context) => {
			const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
			const emailClient: EmailClient = getEffectiveEmailClient();
			return emailClient.supportsEditDraft
				&& documents.length === 1
				&& !documents[0].isFolder;
		},
		execute: (context) => {
			const emailClient: EmailClient = getEffectiveEmailClient();
			if (!emailClient.supportsEditDraft) {
				throw new Error("Current email app launcher does not support edit drafts.");
			}
			sendWebDavFileAsAttachment(
				context.server.id,
				context.selectedDocuments[0].id,
				emailClient
			);
		}
	}
];

export function getSelectionActions(
	handlers: SelectionActionHandlers,
	itemsOnPage: readonly FileSystemItemModel[],
	alertManager: AlertManager,
	editorPreference: UserEditorPreference,
	webDavConfiguration: WebDavConfiguration
): readonly WebDavDocumentAction[] {

	return [
		...getOpenActions(
			handlers,
			itemsOnPage,
			webDavConfiguration,
			alertManager,
			editorPreference
		),
		{
			kind: ActionKind.Regular,
			group: _openGroup,
			name: "setPreference",
			title: localize("_SetPreference"),
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				if (documents.length !== 1) {
					return false;
				}

				if (documents[0].isFolder) {
					return false;
				}

				const { wopi, local } = _openFilePossibilitiesProvider
					.getForFileName(
						documents[0].name,
						webDavConfiguration
					);

				return wopi || local;
			},
			execute: () => {
				openSetOfficePreferenceDialog();
			},
			icon: "folder-open",
		},
		{
			kind: ActionKind.Regular,
			group: _manageGroup,
			name: "Delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				return documents.length > 0;
			},
			execute: (context) => {
				openDeleteDocumentsDialog({
					serverId: context.server.id,
					documentsToDelete: context.selectedDocuments,
					successCallback: (): void => {
						alertManager.showSuccess(localize("_FilesDeletedSuccessfully"));
					}
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _manageGroup,
			name: "Rename",
			title: localize("_Rename"),
			icon: "pen",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				return documents.length === 1;
			},
			execute: (context) => {
				openRenameWebDavDialog({
					serverId: context.server
						.id,
					document: context.selectedDocuments[0]
				});
			}
		},
		{
			kind: ActionKind.Regular,
			group: _manageGroup,
			name: "Move",
			title: localize("_MoveTo"),
			icon: "arrows-up-down-left-right",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				return documents.length > 0;
			},
			execute: (context) => handlers.onMoveItems(context.selectedDocuments)
		},
		{
			kind: ActionKind.Regular,
			group: _manageGroup,
			name: "Copy",
			title: localize("_CopyTo"),
			icon: "copy",
			canExecute: (context) => {
				const { allowCrossStoreOperations } = documentsConfiguration();
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				const isSingleFile: boolean = documents.length === 1 && !documents[0].isFolder;

				return isSingleFile || (allowCrossStoreOperations && documents.length > 0);
			},
			execute: (context) => handlers.onCopyItems(context.selectedDocuments)
		},
		_downloadAction,
		createAddBookmarkAction(handlers.addBookmark),
		createRemoveBookmarkAction(alertManager),
		..._shareActions
	];
}

export type OpenActionHandlers = {
	openFolder(folder: FileSystemItemModel): void;
	selectItem(item: FileSystemItemModel): void;
};

export function getOpenActions(
	handlers: OpenActionHandlers,
	itemsOnPage: readonly FileSystemItemModel[],
	webDavConfiguration: WebDavConfiguration,
	alertManager: AlertManager,
	editorPreference?: UserEditorPreference
): readonly WebDavDocumentAction[] {

	return [
		{
			kind: ActionKind.Regular,
			name: "OpenFolder",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				if (documents.length !== 1) {
					return false;
				}

				return documents[0].isFolder;
			},
			execute: (context) => {
				// We assume always the current server
				handlers.openFolder(context.selectedDocuments[0]);
			}
		},
		...getOpenOfficeDocActions(
			editorPreference,
			webDavConfiguration
		),
		{
			kind: ActionKind.Regular,
			name: "open-url-file",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				if (documents.length !== 1) {
					return false;
				}

				if (documents[0].isFolder) {
					return false;
				}

				return _openFilePossibilitiesProvider
					.getForFileName(
						documents[0].name,
						webDavConfiguration
					).externalUrl;
			},
			execute: async (context) => {
				const serverId: number = context.server.id;
				const file: FileSystemItemModel = context.selectedDocuments[0];

				const url: string = getWebDavFileRelativeUrl(file.id, serverId);
				await openUrlFile(url, alertManager);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "OpenInBrowser",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
				if (documents.length !== 1) {
					return false;
				}

				if (documents[0].isFolder) {
					return false;
				}

				return _openFilePossibilitiesProvider
					.getForFileName(
						documents[0].name,
						webDavConfiguration
					).browser;
			},
			execute: (context) => {
				const serverId: number = context.server.id;
				const file: FileSystemItemModel = context.selectedDocuments[0];
				downloadInline(serverId, file.id);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "OpenInImagePreviewDialog",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const documents: readonly FileSystemItemModel[] = context.selectedDocuments;

				if (documents.length !== 1) {
					return false;
				}

				if (documents[0].isFolder) {
					return false;
				}

				return _openFilePossibilitiesProvider
					.getForFileName(
						documents[0].name,
						webDavConfiguration
					).imagePreviewDialog;
			},
			execute: (context) => {
				const image: FileSystemItemModel = context.selectedDocuments[0];
				const allImages: FileSystemItemModel[] = itemsOnPage
					.filter(
						d => _openFilePossibilitiesProvider
							.getForFileName(
								d.name,
								webDavConfiguration
							).imagePreviewDialog
					);

				const serverId: number = context.server.id;

				openImagePreview(
					serverId,
					image,
					allImages,
					(item) => handlers.selectItem(item),
					context
				);
			}
		}
	];
}

function getOpenOfficeDocActions(
	editorPreference: UserEditorPreference | undefined,
	webDavConfiguration: WebDavConfiguration
): readonly WebDavDocumentAction[] {
	return getItemsInPreferredOrder<WebDavDocumentAction>(
		webDavConfiguration.defaultOfficeDocumentEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => ({
					kind: ActionKind.Regular,
					name: "OpenInOnlineEditor",
					group: _openGroup,
					title: (c) => {
						const { selectedDocuments } = c;
						if (selectedDocuments.length !== 1
							|| selectedDocuments[0].isFolder
						) {
							return "";
						}

						const extension: string = getExtension(selectedDocuments[0].name);

						return getOpenLocalizationsForExtension(extension)
							.online
							.action;
					},
					icon: "folder-open",
					canExecute: (context) => {
						const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
						if (documents.length !== 1) {
							return false;
						}

						if (documents[0].isFolder) {
							return false;
						}

						return _openFilePossibilitiesProvider
							.getForFileName(
								documents[0].name,
								webDavConfiguration
							).wopi;
					},
					execute: (context) => {
						const serverId: number = context.server
							.id;

						const file: FileSystemItemModel = context.selectedDocuments[0];

						openInWopi(serverId, file.id, file.name);
					}
				})
			],
			[
				OfficeDocumentEditor.Local,
				() => ({
					kind: ActionKind.Regular,
					name: "OpenInLocalApp",
					group: _openGroup,
					title: (c) => {
						const { selectedDocuments } = c;
						if (selectedDocuments.length !== 1
							|| selectedDocuments[0].isFolder
						) {
							return "";
						}

						const extension: string = getExtension(selectedDocuments[0].name);

						return getOpenLocalizationsForExtension(extension)
							.local
							.action;
					},
					icon: "folder-open",
					canExecute: (context) => {
						const documents: readonly FileSystemItemModel[] = context.selectedDocuments;
						if (documents.length !== 1) {
							return false;
						}

						if (documents[0].isFolder) {
							return false;
						}

						return _openFilePossibilitiesProvider
							.getForFileName(
								documents[0].name,
								webDavConfiguration
							).local;
					},
					execute: (context) => {
						const document: FileSystemItemModel = context.selectedDocuments[0];
						openInLocalApp(context.server, document);
						const isWopiAvailable: boolean = _openFilePossibilitiesProvider
							.getForFileName(
								document.name,
								webDavConfiguration
							).wopi;

						if (isWopiAvailable) {
							const extension: string = getExtension(document.name);
							const localizations: OpenLocalizations = getOpenLocalizationsForExtension(extension);

							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								localizations,
								() => openInWopi(context.server.id, document.id, document.name)
							);
						}
					}
				})
			]
		],
		editorPreference
	);
}

export function getActionsForLiveTile(
	context: WebDavDocumentActionContext,
	handlers: OpenActionHandlers,
	webDavConfiguration: WebDavConfiguration,
	alertManager: AlertManager
): readonly WebDavDocumentAction[] {
	return [
		...getOpenActions(
			handlers,
			context.selectedDocuments,
			webDavConfiguration,
			alertManager
		),
		_downloadAction,
		..._shareActions
	];
}

export function openImagePreview(
	serverId: number,
	imageItem: FileSystemItemModel,
	allImageItems: readonly FileSystemItemModel[],
	currentImageChangedCallback?: (file: FileSystemItemModel) => void,
	context?: WebDavDocumentActionContext
) {
	openImageDialog(
		{
			serverId,
			initialImage: imageItem,
			allImageItems,
			currentImageChangedCallback,
			initialContext: context
		}
	);
}

export function getFileDownloadUrl(
	fileId: string,
	serverId: number
): string {
	return createUrlRelativeToEnvironment("Documents/WebDav/" + serverId + "/DownloadFile?fileId=" + encodeURIComponent(fileId));
}

export function getWebDavFileRelativeUrl(
	fileId: string,
	serverId: number
): string {
	const url: string = serverId + "/OpenFileInline?fileId=" + encodeURIComponent(fileId);
	return "Documents/WebDav/" + url;
}

export function downloadInline(serverId: number, fileId: string): void {
	const openFileUrl: string = createUrlRelativeToEnvironment(
		getWebDavFileRelativeUrl(fileId, serverId)
	);

	openInline(openFileUrl);
}

export function getImagePreviewActions(
	alertManager: AlertManager,
	addBookmarkHandler: AddBookmarkHandler
): readonly Action<WebDavDocumentActionContext>[] {
	return [
		_downloadAction,
		..._shareActions,
		createAddBookmarkAction(addBookmarkHandler),
		createRemoveBookmarkAction(alertManager)
	];
}
